import { AgendamentosDia } from "@/components/AgendamentosDia";
import { useAuth } from "@/context/AuthProvider";
import { cn } from "@/lib/utils";
import { AgendamentoService, type AgendamentoDiaResponse } from "@/services/app/agendamento";
import { useQuery } from "@tanstack/react-query";
import { Bell, Calendar, Clock, Megaphone, PawPrint } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { PetCardMiniature } from "./petcard";

function Home(): React.JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();

  const agendamentoService = new AgendamentoService();

  const { data: agendamentosDia, isLoading } = useQuery<AgendamentoDiaResponse>({
    queryKey: ["agendasDia", user?.id],
    queryFn: async () => {
      const response = await agendamentoService.getAgendamentoDia();
      return response;
    },
  });

  const menu = [
    {
      itemName: "Agendamentos",
      icon: Calendar,
      route: "/agendamentos",
      variant: "primary" as const,
    },
    {
      itemName: "Campanhas",
      icon: Megaphone,
      route: "/campanhas",
      variant: "quinary" as const,
      isNew: false,
    },
    {
      itemName: "Meus Pets",
      icon: PawPrint,
      route: "/pets",
      variant: "secondary" as const,
    },
    {
      itemName: "Histórico",
      icon: Clock,
      route: "/historico",
      variant: "tertiary" as const,
    },
    {
      itemName: "Notificações",
      icon: Bell,
      route: "/notifications",
      variant: "quaternary" as const,
    },
  
  ];

  const getVariantStyles = (variant: string) => {
    switch (variant) {
      case "primary":
        return "bg-blue-600 hover:bg-blue-700";
      case "secondary":
        return "bg-orange-600 hover:bg-orange-700";
      case "tertiary":  
        return "bg-green-600 hover:bg-green-700";
      case "quaternary":
        return "bg-purple-600 hover:bg-purple-700";
      case "quinary":
        return "bg-indigo-600 hover:bg-indigo-700";
      default:
        return "bg-blue-600 hover:bg-blue-700";
    }
  };

  return (
    <div className="space-y-6">
      {/* Saudação */}
      <div className="px-2 mt-3">
        <h1 className="text-2xl sm:text-3xl leading-tight">
          <span className="font-bold text-blue-600">Olá,</span>
          <br />
          <span className="text-gray-800">
            {`${user?.nome.split(" ")[0]} ${user?.nome.split(" ")[1] ?? ""}`}
          </span>
        </h1>
        <p className="text-gray-500 mt-2 text-sm sm:text-base">
          Bem-vindo ao seu portal de agendamentos
        </p>
      </div>

      {/* Status Card - Agendamentos do dia */}
      <AgendamentosDia data={agendamentosDia} isLoading={isLoading} />

      {/* Menu Principal */}
      <div>
        <h2 className="text-lg font-bold mb-4 text-gray-800">
          Menu Principal
        </h2>
        
        {/* Grid de botões do menu - responsivo */}
        <div className="grid grid-cols-2 gap-3">
          {menu.map((item, index) => (
            <button
              key={index}
              onClick={() => navigate(item.route)}
              className={cn(
                "rounded-xl p-4 h-28 sm:h-32 text-white transition-all duration-200 active:scale-95 hover:scale-105 touch-manipulation shadow-sm relative",
                getVariantStyles(item.variant)
              )}
            >
              {item.isNew && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg">
                  NOVO
                </div>
              )}
              <div className="flex flex-col justify-between h-full text-left">
                <div className="mb-2">
                  {typeof item.icon === "string" ? (
                    <span className="text-xl sm:text-2xl">{item.icon}</span>
                  ) : (
                    <item.icon size={20} className="sm:w-6 sm:h-6" />
                  )}
                </div>
                <div>
                  <h3 className="text-sm sm:text-lg font-medium leading-tight">
                    {item.itemName}
                  </h3>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Card do Pet */}
      <PetCardMiniature
        cardNumber={user?.documento ?? ""}
        ownerName={user?.nome ?? ""}
      />
    </div>
  );
}

export default Home;
